import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { useSelector, useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import { useIntl } from 'react-intl';

import styles from './PasswordAccountCreation.module.scss';
import validator from './validator';
import messages from './messages';
import InputPassword from '../../common/InputPassword';
import onSignup from '../common-shared-attributes/OnSignUp';
import InputText from '../../common/InputText';
import LinkButton from '../../common/LinkButton';
import AccountCreationError from './AccountCreationError';
import {
  setCustomerId,
  setLoadResetPasswordComponent,
  setShowResetModalByDefault,
} from '../../../slices/accounts';
import LoginModal from '../../LoginModal';

const PasswordAccountCreation = ({
  accountSuccessfullyCreated = false,
  setAccountSuccessfullyCreated = () => {},
  openLoginModal = () => {},
  setErrorMessage = () => {},
  customerAddress = [],
}) => {
  const { formatMessage } = useIntl();
  const [emailExistsError, setEmailExistsError] = useState(false);
  const [accountCreationError, setAccountCreationError] = useState(false);
  const userDetails = useSelector((state) => state.order.shippingAddress);
  const {
    showLoginModal,
    loadResetPasswordComponent,
  } = useSelector(
    (state) => state.accounts,
  );
  const dispatch = useDispatch();

  const initialValues = {
    givenName: userDetails?.firstName,
    familyName: userDetails?.lastName,
    email: userDetails?.email,
    phoneNumber: userDetails?.phone,
    password: '',
  };

  const anonymousId = useSelector((state) =>
    state.cart?.anonymousId || state.order?.anonymousId);
  const {
    values, errors, handleChange, handleBlur, handleSubmit, isValid,
  } =
    useFormik({
      initialValues,
      enableReinitialize: true,
      onSubmit: () => {
        onSignup(values, {
          emailExistsError,
          setEmailExistsError,
          setAccountSuccessfullyCreated,
          setErrorMessage,
          customerAddress,
          setAccountCreationError,
          anonymousId,
        });
      },
      validate: (v) => validator(v, { formatMessage }),
    });

  const setLoggedInCustomerId = useCallback(
    (loggedInCustomerId) => {
      dispatch(setCustomerId(loggedInCustomerId));
    },
    [dispatch],
  );

  return (
    <>
      <form onSubmit={handleSubmit}>
        <InputText
          id="givenName"
          type="hidden"
          name="givenName"
          value={userDetails?.firstName}
          errorMsg={errors.givenNameError}
        />
        <InputText
          id="familyName"
          type="hidden"
          name="familyName"
          value={userDetails?.lastName}
          errorMsg={errors.familyNameError}
        />
        <InputText
          id="email"
          type="hidden"
          name="email"
          value={userDetails?.email}
          errorMsg={errors.emailError}
        />
        <InputText
          id="phoneNumber"
          type="hidden"
          name="phoneNumber"
          value={userDetails?.phone}
        />
        {!accountSuccessfullyCreated && (
          <>
            <div className={styles['signup-input-field']}>
              <InputPassword
                id="password"
                name="password"
                label={formatMessage(messages.passwordLabel)}
                handleOnChange={handleChange}
                handleOnBlur={handleBlur}
                value={values.password}
                isValid={isValid}
                errorMsg={errors.passwordError}
              />
            </div>
            <input
              type="submit"
              className={classnames('btn', styles['signup-btn'])}
              value={formatMessage(messages.submitButton)}
            />
          </>
        )}
        {accountCreationError && (
          <AccountCreationError
            accountCreationError={accountCreationError}
            customerEmail={userDetails?.email}
          />
        )}
        {emailExistsError && (
          <div className={styles['email-exists-error']}>
            {formatMessage(messages.emailExistsErrorMsg)}
            <span className={styles['login-link']}>
              <LinkButton
                id="emailErrLoginLink"
                onClick={openLoginModal}
                isError
                linkText={formatMessage(messages.emailExistsLoginText)}
              />
            </span>
            {formatMessage(messages.emailExistsOrText)}
            <span className={styles['login-link']}>
              <LinkButton
                id="loadForgotPasswordComponent"
                onClick={() => {
                  dispatch(setLoadResetPasswordComponent({ loadResetPasswordComponent: true }));
                  dispatch(setShowResetModalByDefault({ showResetModalByDefault: true }));
                }}
                linkText={formatMessage(messages.emailExistsResetText)}
                isError
              />
            </span>
          </div>
        )}
      </form>
      {(showLoginModal || loadResetPasswordComponent) && (
        <LoginModal
          setLoggedInCustomerId={setLoggedInCustomerId}
          mergeCart
          redirectToAccountDashboard
        />
      )}
    </>
  );
};

PasswordAccountCreation.propTypes = {
  accountSuccessfullyCreated: PropTypes.bool,
  setAccountSuccessfullyCreated: PropTypes.func,
  openLoginModal: PropTypes.func,
  setErrorMessage: PropTypes.func,
  customerAddress: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
};

export default PasswordAccountCreation;
